import type { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { CommonActions } from '@react-navigation/native';
import type { FC } from 'react';
import { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import {
  Badge,
  BottomNavigation,
  Surface,
  TouchableRipple
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { BottomTabbarLabel } from '@/navigation/components/tab/BottomTabbarLabel';
import {
  DARK_TAB_HIGHLIGHTS,
  highlightColor,
  TAB_HIGHLIGHTS
} from '@/navigation/components/tab/tabHighlights';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const BOTTOM_INSET_FACTOR = 0.7;
const HIGHLIGHT_RADIUS = 12;
// Fits Paper's item content (12 padding + 32 icon + 16 label).
const HIGHLIGHT_HEIGHT = 56;
const PILL_INSET = 4;
// Room below the pill so the underline sits under the rectangle.
const UNDERLINE_SPACE = 8;
const TAB_HEIGHT = PILL_INSET + HIGHLIGHT_HEIGHT + UNDERLINE_SPACE;
const HIGHLIGHT_GAP = 2;
const DOT_BADGE = ' ';
const EMPTY_BADGE = '0';

const ACTIVE_INDICATOR_HEIGHT = 3;

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    bar: {
      borderTopWidth: 0,
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    activeIndicator: { backgroundColor: 'transparent' },
    tabWrapper: {
      // Paper's item style (flex: 1) lands here via the row; without it each
      // tab would shrink to its content and the bar would center as a group.
      flex: 1,
      height: TAB_HEIGHT,
      alignItems: 'stretch',
      justifyContent: 'flex-start'
    },
    // A view's own background ripple is drawn before its dispatchDraw, so
    // overflow: hidden on the touchable cannot clip it. A parent clipper
    // clips its children's drawing entirely, giving the ripple rounded
    // corners on Android.
    pillClipper: {
      marginTop: PILL_INSET,
      marginHorizontal: HIGHLIGHT_GAP,
      borderRadius: HIGHLIGHT_RADIUS,
      height: HIGHLIGHT_HEIGHT,
      overflow: 'hidden'
    },
    touchable: {
      height: '100%',
      justifyContent: 'center',
      borderRadius: HIGHLIGHT_RADIUS
    },
    activeIndicatorBar: {
      position: 'absolute',
      bottom: 0,
      alignSelf: 'center',
      width: '50%',
      height: ACTIVE_INDICATOR_HEIGHT,
      borderTopLeftRadius: ACTIVE_INDICATOR_HEIGHT,
      borderTopRightRadius: ACTIVE_INDICATOR_HEIGHT,
      backgroundColor: theme.colors.primary
    },
    // Paper's v3 items carry paddingTop: 12 / paddingBottom: 16; with a fixed
    // wrapper height that padding would push the pill off the bar.
    tabContent: {
      paddingTop: 0,
      paddingBottom: 0,
      height: HIGHLIGHT_HEIGHT,
      justifyContent: 'center'
    },
    badge: {
      position: 'absolute',
      top: -4,
      right: -8,
      zIndex: 1,
      fontSize: 10,
      fontWeight: '500'
    },
    dot: {
      position: 'absolute',
      top: -4,
      right: -8,
      zIndex: 1
    }
  });

export const BottomTabbar: FC<BottomTabBarProps> = ({
  navigation,
  state,
  descriptors,
  insets
}) => {
  const theme = useAppTheme();
  const { bottom } = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const highlights = theme.dark ? DARK_TAB_HIGHLIGHTS : TAB_HIGHLIGHTS;

  return (
    <Surface elevation={5}>
      <BottomNavigation.Bar
        shifting={false}
        style={styles.bar}
        inactiveColor={theme.colors.primary}
        activeColor={theme.colors.primary}
        activeIndicatorStyle={styles.activeIndicator}
        navigationState={state}
        safeAreaInsets={{ ...insets, bottom: bottom * BOTTOM_INSET_FACTOR }}
        onTabPress={(tab) => {
          const event = navigation.emit({
            type: 'tabPress',
            target: tab.route.key,
            canPreventDefault: true
          });
          if (event.defaultPrevented) {
            tab.preventDefault();
          } else {
            navigation.dispatch({
              ...CommonActions.navigate(tab.route.name, tab.route.params),
              target: state.key
            });
          }
        }}
        renderTouchable={({ key, route, style, children, ...rest }) => {
          const highlight = highlights[route.name];
          const focused =
            state.index === state.routes.findIndex((r) => r.key === route.key);

          return (
            <View key={key} style={styles.tabWrapper}>
              <View style={styles.pillClipper}>
                <TouchableRipple
                  {...rest}
                  testID={rest.testID ?? `tab-${route.name}`}
                  rippleColor={theme.rippleColors.onSurface}
                  background={{
                    color: theme.rippleColors.onSurface,
                    borderless: false
                  }}
                  style={[
                    style,
                    styles.tabContent,
                    styles.touchable,
                    highlight
                      ? { backgroundColor: highlightColor(theme, highlight) }
                      : undefined
                  ]}
                >
                  {children}
                </TouchableRipple>
              </View>
              {focused && (
                <View
                  testID={`tab-${route.name}-indicator`}
                  style={styles.activeIndicatorBar}
                />
              )}
            </View>
          );
        }}
        renderIcon={({ route, focused }) => {
          const { options } = descriptors[route.key];
          const { tabBarIcon, tabBarBadge, tabBarBadgeStyle } = options;
          const badgeValue = tabBarBadge?.toString();

          return (
            <View>
              {badgeValue === DOT_BADGE && (
                <Badge
                  visible
                  style={[styles.dot, tabBarBadgeStyle]}
                  size={8}
                />
              )}
              {badgeValue != undefined &&
                badgeValue !== DOT_BADGE &&
                badgeValue !== EMPTY_BADGE && (
                  <Badge
                    maxFontSizeMultiplier={1}
                    ellipsizeMode="tail"
                    style={[styles.badge, tabBarBadgeStyle]}
                  >
                    {tabBarBadge}
                  </Badge>
                )}
              {tabBarIcon?.({
                focused,
                size: 24,
                color: theme.colors.primary
              })}
            </View>
          );
        }}
        renderLabel={({ route }) => {
          const { options } = descriptors[route.key];
          const label = (options.tabBarLabel as string) ?? options.title;

          return label ? (
            <BottomTabbarLabel
              label={label}
              testID={`tab-${route.name}-label`}
            />
          ) : (
            <></>
          );
        }}
      />
    </Surface>
  );
};
